import React, { useRef } from "react";
import { Container, Row, Col, Badge } from "react-bootstrap";
import { motion } from "framer-motion";
import {
    ArrowRight,
    Info,
    Factory,
    LineChart,
    BadgePercent,
    HardHat,
    Map,
    Building2,
    Layers3,
    DollarSign,
} from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import KPISection from "../components/KPISections";
import WorkforceChart from "../components/WorkforceChart";
import NaicsTrend from "../components/NaicsTrend";
import CountyTable from "../components/CountyTable";
import ProjectsSection from "../components/ProjectsSection";
import CostIndicators from "../components/CostIndicators";
import PDFExportButton from "../components/PDFExportButton";
import PermitsOverview from "../components/PermitsOverview";
import InfrastructureGIS from "../components/InfrastructureGIS";
import MarketProjections from "../components/MarketProjections";
import OccupationsTable from "../components/OccupationsTable";
import SectorAnalysis from "../components/SectorAnalysis";
import HistoricalWorkforce from "../components/HistoricalWorkforce";
import SpendingTrends from "../components/SpendingTrends";
import SubsectorDetail from "../components/SubsectorDetail";
import TopCompanies from "../components/TopCompanies";
import EconomicImpact from "../components/EconomicImpact";
import data from "../data/constructionData.json";

// Animation Variants
const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const SectionPanel = ({ id, icon: Icon, eyebrow, title, subtitle, children }) => {
    return (
        <section id={id} className="mb-16 scroll-mt-28">
            <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-4">
                <div className="d-flex align-items-start gap-3">
                    <div
                        className="rounded-2xl p-3 shadow-sm border border-gray-100"
                        style={{
                            background: "linear-gradient(180deg, rgba(255,255,255,0.9), rgba(249,250,251,1))",
                        }}
                    >
                        <Icon size={20} className="text-indigo-600" />
                    </div>

                    <div>
                        {eyebrow ? (
                            <div className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
                                {eyebrow}
                            </div>
                        ) : null}
                        <h2 className="text-2xl font-black text-gray-200 m-0">{title}</h2>
                        {subtitle ? <p className="text-gray-500 text-sm mt-1 mb-0">{subtitle}</p> : null}
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-4 sm:p-6">
                {children}
            </div>
        </section>
    );
};

const JumpLink = ({ href, children }) => (
    <a
        href={href}
        className="text-decoration-none text-xs font-semibold text-white/90 hover:text-white bg-white/10 hover:bg-white/15 px-3 py-2 rounded-full border border-white/15 transition-all"
    >
        {children}
    </a>
);

const ConstructionDashboard = () => {
    const pdfRef = useRef();

    return (
        <div
            className="min-h-screen flex flex-col"
            style={{
                color: "#333",
                background:
                    "radial-gradient(1200px 600px at 15% 0%, rgba(99, 102, 241, 0.10), transparent 60%), radial-gradient(900px 500px at 90% 12%, rgba(16, 185, 129, 0.10), transparent 55%), #f8fafc",
            }}
        >
            <Header />

            <div ref={pdfRef} className="flex-grow">
                {/* --- HERO SECTION --- */}
                <div style={{ position: "relative" }}>
                    <div
                        style={{
                            background: `linear-gradient(135deg, rgba(11, 18, 32, 0.80) 0%, rgba(30, 41, 59, 0.92) 100%), url(/img/landing-webp/construction_hero_new.png)`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            color: "white",
                            padding: "5.5rem 0 8rem 0",
                            clipPath: "polygon(0 0, 100% 0, 100% 86%, 0 100%)",
                            marginBottom: "3rem",
                        }}
                    />

                    {/* soft glow accents */}
                    <div
                        aria-hidden="true"
                        style={{
                            position: "absolute",
                            inset: 0,
                            pointerEvents: "none",
                            background:
                                "radial-gradient(600px 260px at 20% 30%, rgba(99, 102, 241, 0.35), transparent 60%), radial-gradient(600px 260px at 80% 25%, rgba(16, 185, 129, 0.22), transparent 60%)",
                            mixBlendMode: "screen",
                        }}
                    />

                    <Container>
                        <motion.div initial="hidden" animate="visible" variants={fadeIn}>
                            <Row className="align-items-center text-center" style={{ position: "relative", marginTop: "-10.5rem" }}>
                                <Col lg={10} className="mx-auto">
                                    <Badge
                                        bg="danger"
                                        className="mb-3 px-3 py-2 text-uppercase fw-bold rounded-pill"
                                        style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}
                                    >
                                        2026 Construction Reawakening
                                    </Badge>
                                    <h1 className="display-4 fw-bold mb-3 tracking-tight" style={{ textShadow: "0 8px 28px rgba(255, 255, 255, 0.35)" }}>
                                        Utah Construction Intelligence
                                    </h1>
                                    <p className="lead opacity-90 mb-4 px-lg-5 fw-light text-white">
                                        {data.subtitle}
                                    </p>

                                    <div className="d-flex justify-content-center gap-2 flex-wrap mb-4">
                                        <JumpLink href="#kpis">KPIs</JumpLink>
                                        <JumpLink href="#economic-impact">Economic impact</JumpLink>
                                        <JumpLink href="#permits-workforce">Permits & workforce</JumpLink>
                                        <JumpLink href="#projects-counties">Projects & counties</JumpLink>
                                        <JumpLink href="#sources">Sources</JumpLink>
                                    </div>

                                    <div className="d-flex justify-content-center gap-3 flex-wrap">
                                        <div
                                            className="px-4 py-2 rounded-full border border-white/20 bg-white/10 text-white text-sm fw-semibold"
                                            style={{ backdropFilter: "blur(8px)" }}
                                        >
                                            {data.title}
                                        </div>
                                        {/* If you want PDF export visible in hero, uncomment this */}
                                        {/* <PDFExportButton pdfRef={pdfRef} /> */}
                                    </div>
                                </Col>
                            </Row>
                        </motion.div>
                    </Container>
                </div>

                <Container style={{ marginTop: "-4rem", position: "relative", zIndex: 10 }} className="pb-12">
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeIn}>
                        <SectionPanel
                            id="kpis"
                            icon={LineChart}
                            eyebrow="At-a-glance"
                            title="Key Performance Indicators"
                            subtitle="The fastest way to understand market scale, spending, and near-term momentum."
                        >
                            <KPISection />
                        </SectionPanel>

                        <SectionPanel
                            id="top-companies"
                            icon={Factory}
                            eyebrow="Industry landscape"
                            title="Top Construction Companies"
                            subtitle="Key players shaping Utah’s construction capacity and delivery."
                        >
                            <TopCompanies data={data.top_10_construction_companies.companies_details} />
                        </SectionPanel>

                        <SectionPanel
                            id="economic-impact"
                            icon={BadgePercent}
                            eyebrow="Comparison"
                            title="Utah vs. U.S. Economic Impact"
                            subtitle="Side-by-side metrics that make the scale difference instantly clear."
                        >
                            <EconomicImpact data={data.table_1_utah_vs_us_economic_impact_2025} />
                        </SectionPanel>

                        <SectionPanel
                            id="permits-workforce"
                            icon={HardHat}
                            eyebrow="Pipeline & labor market"
                            title="Permits and Workforce"
                            subtitle="Leading indicators + labor conditions to gauge capacity and future supply."
                        >
                            <div className="mb-8">
                                <PermitsOverview data={data.table_3_permits_overview_2025} />
                            </div>
                            <Row className="g-4">
                                <Col lg={6}>
                                    <WorkforceChart data={data.table_4_workforce_labor_market_2025} />
                                </Col>
                                <Col lg={6}>
                                    <NaicsTrend data={data.naics_employment_trends} />
                                </Col>
                            </Row>
                        </SectionPanel>

                        <SectionPanel
                            id="workforce-detail"
                            icon={Layers3}
                            eyebrow="Depth"
                            title="Workforce Detail"
                            subtitle="Specialization, historical patterns, and the occupations most in demand."
                        >
                            <div className="mb-10">
                                <SubsectorDetail data={data.utah_construction_subsector_comparison_2026_est} />
                            </div>
                            <div className="mb-10">
                                <HistoricalWorkforce data={data.yearwise_workforce_metrics_2018_2024} />
                            </div>
                            <OccupationsTable data={data.top_construction_occupations_2025} />
                        </SectionPanel>

                        <SectionPanel
                            id="infrastructure-gis"
                            icon={Map}
                            eyebrow="Built environment"
                            title="Infrastructure & GIS"
                            subtitle="Where projects are landing and what the spatial data says about future build-out."
                        >
                            <InfrastructureGIS data={data.table_5_infrastructure_gis_2026} />
                        </SectionPanel>

                        <SectionPanel
                            id="projects-counties"
                            icon={Building2}
                            eyebrow="Execution"
                            title="Major Projects and County Metrics"
                            subtitle="Project pipeline + local performance breakdown."
                        >
                            <Row className="g-4">
                                <Col lg={12}>
                                    <ProjectsSection data={data.table_6_major_projects} />
                                </Col>
                                <Col lg={12}>
                                    <CountyTable data={data.table_8_county_metrics_2025} />
                                </Col>
                            </Row>
                        </SectionPanel>

                        <SectionPanel
                            id="market-projections"
                            icon={DollarSign}
                            eyebrow="Forward look"
                            title="Market Projections and Sector Analysis"
                            subtitle="Where spending and market mix are trending over the next cycle."
                        >
                            <div className="mb-10">
                                <SpendingTrends data={data.yearwise_spending_metrics} />
                            </div>
                            <div className="mb-10">
                                <MarketProjections
                                    dataProjections={data.table_12_market_value_billions}
                                    dataHistory={data.table_13_market_size_2020_2030}
                                />
                            </div>
                            <SectorAnalysis
                                sectorComp={data.table_10_sector_comparison}
                                marketShare={data.table_11_market_share_by_type}
                            />
                        </SectionPanel>

                        <SectionPanel
                            id="cost-indicators"
                            icon={LineChart}
                            eyebrow="Inflation & inputs"
                            title="Cost Indicators"
                            subtitle="Material and cost pressure signals that affect feasibility and margins."
                        >
                            <CostIndicators data={data.table_7_cost_inflation} />
                        </SectionPanel>
                    </motion.div>

                    {/* DATA SOURCES */}
                    <motion.div
                        id="sources"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeIn}
                        className="text-center mt-20 py-10 bg-white rounded-3xl shadow-sm border border-gray-100"
                    >
                        <p className="text-gray-400 text-sm font-semibold uppercase tracking-widest mb-4 d-flex align-items-center justify-content-center gap-2">
                            <Info size={16} /> Dashboard Intel & Data Sources
                        </p>
                        <div className="d-flex justify-content-center gap-3 flex-wrap px-4">
                            {data.sources.map((source, idx) => (
                                <a
                                    key={idx}
                                    href={source}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-decoration-none text-xs text-gray-600 bg-gray-50 hover:bg-gray-100 px-4 py-2 rounded-full border border-gray-200 transition-all d-flex align-items-center gap-1"
                                >
                                    {source.startsWith("http") ? new URL(source).hostname.replace("www.", "") : source}
                                    <ArrowRight size={12} className="opacity-50" />
                                </a>
                            ))}
                        </div>
                    </motion.div>
                </Container>
            </div>

            <Footer />
        </div >
    );
};

export default ConstructionDashboard;
